/* eslint-disable react-hooks/exhaustive-deps */
import { Button } from "@material-ui/core";
import TextField from "@material-ui/core/TextField";
import { FilterListOutlined } from "@material-ui/icons";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Autocomplete from "../../components/Autocomplete";
import DateRangePicker from "../../components/DateRangePicker";
import Search from "../../components/Search";
import { useNotification } from "../../context/notification";
import { apiRequest } from "../../utils/api";
import { getLocalValue } from "../../utils/auth";
import styles from "./Logs.module.scss";
import { useLogsFilters } from "./useLogsFilters";

export function LogsFilterBarV2({
  storageKey,
  onChange,
  selectedCampaign,
  selectedCountry,
  dateRange = false,
  type,
  subject,
  coach,
  coachee,
  status,
}) {
  const { t } = useTranslation();
  const { addNotification } = useNotification();
  const userRole = getLocalValue("u_coaching_role_db");
  const f = useLogsFilters(storageKey);

  const [subjectList, setSubjectList] = useState([]);
  const [coachNames, setCoachNames] = useState([]);
  const [coacheeNames, setCoacheeNames] = useState([]);
  const [statusList, setStatusList] = useState([]);

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });
  useEffect(() => {
    onChangeRef.current?.({
      showFilter: f.showFilter,
      selectedDateRange: f.selectedDateRange,
      selectedType: f.selectedType,
      selectedSubject: f.selectedSubject,
      selectedCoachNames: f.selectedCoachNames,
      selectedNames: f.selectedNames,
      selectedStatus: f.selectedStatus,
      searchKey: f.searchKey,
    });
  }, [
    f.showFilter,
    f.selectedDateRange,
    f.selectedType,
    f.selectedSubject,
    f.selectedCoachNames,
    f.selectedNames,
    f.selectedStatus,
    f.searchKey,
  ]);

  async function fetchList(url, setter) {
    const response = await apiRequest.get(url);
    if (response.status === 200) {
      setter(response.data.data);
    } else {
      addNotification(
        t("error.couldNotGetSession") + ` (Status code: ${response.status})`,
        "error"
      );
    }
  }

  useEffect(() => {
    if (!selectedCampaign || !selectedCountry) return;
    const base = `user_role=${userRole}&campaign_id=${selectedCampaign}&country_id=${selectedCountry}`;
    if (type || subject) {
      fetchList(`api/getSubjectForLogFilter?${base}`, setSubjectList);
    }
    if (coach) {
      fetchList(`api/getCoachList?${base}`, setCoachNames);
    }
    if (coachee) {
      fetchList(`api/getCoacheeLists?${base}`, setCoacheeNames);
    }
    if (status) {
      fetchList(`api/getSupportAdminStatusCode`, setStatusList);
    }
  }, [selectedCampaign, selectedCountry]);

  // "Type" options are the subjects deduped by their `type`.
  const typeOptions = Array.from(
    new Set(subjectList.map(o => o.type))
  ).map(tp => subjectList.find(o => o.type === tp));

  return (
    <div className={styles.filterWrapper}>
      <div className={styles.search}>
        <Search
          searchInputValue={f.searchInputValue}
          handleSearch={val => f.setSearchKey(val)}
        />
      </div>
      <div
        onClick={() => f.setShowFilter(!f.showFilter)}
        className={styles.filterButton}
      >
        {t("logs.filter")}
        <FilterListOutlined />
      </div>
      {f.showFilter && (
        <div className={styles.filterRow}>
          {dateRange && (
            <div className={styles.dateRange}>
              <DateRangePicker
                selectedDateRange={f.selectedDateRange}
                handleDateChange={val => f.setSelectedDateRange(val)}
                disableFuture
              />
            </div>
          )}

          {type && (
            <Autocomplete
              value={f.selectedType}
              defaultValue={f.selectedType}
              onChange={(event, newValue) => f.setSelectedType(newValue)}
              options={typeOptions}
              getOptionSelected={(option, value) => option.type === value?.type}
              getOptionLabel={option => option.type}
              style={{ width: 140 }}
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder={type.placeholder ?? t("logs.type")}
                  variant="outlined"
                />
              )}
            />
          )}

          {subject && (
            <Autocomplete
              value={f.selectedSubject}
              defaultValue={f.selectedSubject}
              onChange={(event, newValue) => f.setSelectedSubject(newValue)}
              options={subjectList}
              groupBy={subject.groupBy ?? (option => option.subject)}
              getOptionSelected={(option, value) =>
                option.id === value?.id &&
                option.subjectable_type === value?.subjectable_type
              }
              getOptionLabel={option => option.sub_subject}
              style={{ width: 240 }}
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder={subject.placeholder ?? t("logs.subjects")}
                  variant="outlined"
                />
              )}
            />
          )}

          {coach && (
            <Autocomplete
              value={f.selectedCoachNames}
              defaultValue={f.selectedCoachNames}
              onChange={(event, newValue) => f.setSelectedCoachNames(newValue)}
              options={coachNames}
              getOptionSelected={(option, value) => option.uid === value?.uid}
              getOptionLabel={option => option.coach_name}
              style={{ width: 150 }}
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder={coach.placeholder ?? t("logs.coach")}
                  variant="outlined"
                />
              )}
            />
          )}

          {coachee && (
            <Autocomplete
              value={f.selectedNames}
              defaultValue={f.selectedNames}
              onChange={(event, newValue) => f.setSelectedNames(newValue)}
              options={coacheeNames}
              getOptionSelected={(option, value) => option.uid === value?.uid}
              getOptionLabel={option => option.name}
              style={{ width: 240 }}
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder={coachee.placeholder ?? t("logs.coachee")}
                  variant="outlined"
                />
              )}
            />
          )}

          {status && (
            <Autocomplete
              value={f.selectedStatus}
              defaultValue={f.selectedStatus}
              onChange={(event, newValue) => f.setSelectedStatus(newValue)}
              options={statusList}
              getOptionSelected={(option, value) =>
                option.value === value?.value
              }
              getOptionLabel={option => option.status}
              style={{ width: 140 }}
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder={status.placeholder ?? t("logs.status")}
                  variant="outlined"
                />
              )}
            />
          )}

          <div>
            <Button onClick={f.clearAll}>{t("logs.clearAll")}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
